import { SHOW_TITLE } from '~/utils/show'

// What a ticket costs (also in the FAQ and public/llms.txt)
const TICKET_PRICE = '21.50'

// A description of at most about 155 characters, cut at a word: what search engines show under the title
export const summarize = (text?: string | null, max = 155) => {
  const clean = (text ?? '').replace(/\s+/g, ' ').trim()
  if (clean.length <= max) { return clean }
  const cut = clean.slice(0, max - 1)
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,.;:–-]+$/, '')}…`
}

// Themes that are labels for the storage rather than something people search for
export const LABEL_THEMES = ['basic', 'chique', 'seizoen', 'uitdagend']

// Theme names that keep their capital in a sentence: places, names and nationalities
const PROPER_THEMES = ['tirol', 'moulin rouge', 'dickens', 'halloween', 'grieks', 'italiaans', 'spaans', 'arabisch', 'afrikaans', 'indonesisch', 'egyptisch', 'indiaans']
export const themeInSentence = (title: string) => PROPER_THEMES.includes(title.trim().toLowerCase()) ? title.trim() : title.trim().toLowerCase()

// "rood", "rood en wit", "rood, wit en zwart"
const listNl = (items: string[]) => items.length > 1 ? `${items.slice(0, -1).join(', ')} en ${items.at(-1)}` : (items[0] ?? '')

// The running text of Storyblok blocks (rich text in SbText and the like), headings left out
export const storyText = (value: unknown): string => {
  if (!value || typeof value !== 'object') { return '' }
  if (Array.isArray(value)) { return value.map(storyText).filter(Boolean).join(' ') }
  const node = value as Record<string, any>
  if (node.type === 'heading') { return '' }
  if (node.type === 'text') { return node.text ?? '' }
  if (node.type === 'doc' || node.type === 'paragraph' || node.type === 'bullet_list' || node.type === 'list_item') {
    return storyText(node.content)
  }
  return storyText(node.blocks ?? node.text ?? node.body)
}

// A performance in Storyblok is the time on the clock in Hengelo ("2027-01-29 20:00").
// Structured data wants ISO 8601 with the offset: "2027-01-29T20:00:00+01:00".
const hengeloTime = (wallClock: string, addMinutes = 0) => {
  const [day = '', time = '00:00'] = wallClock.trim().split(/[ T]/)
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number)
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  // The clock values in a UTC date, so adding minutes rolls over days correctly
  const clock = new Date(Date.UTC(year, month - 1, date, hours, minutes + addMinutes))
  const zone = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Amsterdam', timeZoneName: 'shortOffset' })
    .formatToParts(clock).find(part => part.type === 'timeZoneName')?.value ?? 'GMT+1'
  const [, sign = '+', offsetHours = '1', offsetMinutes = '00'] = zone.match(/GMT([+-])(\d+)(?::(\d+))?/) ?? []
  const pad = (value: number | string) => String(value).padStart(2, '0')
  return `${clock.getUTCFullYear()}-${pad(clock.getUTCMonth() + 1)}-${pad(clock.getUTCDate())}T${pad(clock.getUTCHours())}:${pad(clock.getUTCMinutes())}:00${sign}${pad(offsetHours)}:${pad(offsetMinutes)}`
}

export const useHrMeta = () => {
  const config = useRuntimeConfig()
  const baseUrl = 'https://www.hengeloserevue.nl'
  const image = [`${baseUrl}/open-graph-watn-spul.jpg`, `${baseUrl}/open-graph-2.jpg`, `${baseUrl}/open-graph-3.jpg`]

  const description = 'Dé hilarische theatervoorstelling in Twente vol lach, muziek en dans.'
  const address = {
    '@type': 'PostalAddress',
    addressCountry: 'NL',
    addressLocality: 'Hengelo',
    streetAddress: 'Torenlaan 5-3',
    postalCode: '7559 PK'
  }
  const telephone = '074 291 8579'

  const publisher = {
    '@type': ['TheaterGroup', 'EntertainmentBusiness'],
    '@id': `${baseUrl}#Organization`,
    name: 'Hengelose Revue',
    url: baseUrl,
    foundingDate: '1953',
    sameAs: [
      'https://www.facebook.com/hengeloserevue',
      'https://www.instagram.com/hengeloserevue/'
    ],
    logo: {
      '@type': 'ImageObject',
      url: `${baseUrl}/brand/logo/logo-fc.png`,
      width: 2000,
      height: 478
    },
    image,
    address,
    telephone: '+31742918579',
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 52.26174,
      longitude: 6.79367
    },
    priceRange: '€€',
    // No aggregateRating: Google only allows ratings of your own organisation when the reviews are on the page
    hasMap: 'https://maps.google.com/?q=Torenlaan+5-3,+7559+PK+Hengelo'
  }

  const performers = {
    '@type': 'TheaterGroup',
    url: baseUrl,
    name: 'De Hengelose Revue'
  }

  const location = {
    '@type': 'Place',
    name: 'Schouwburg Hengelo',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'NL',
      addressLocality: 'Hengelo',
      streetAddress: 'Beursstraat 44',
      postalCode: '7551 HV'
    },
    url: 'https://www.schouwburghengelo.nl/'
  }

  const website = {
    type: 'application/ld+json',
    innerHTML: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${baseUrl}#WebSite`,
      url: baseUrl,
      description,
      name: 'Hengelose Revue',
      inLanguage: 'nl-NL',
      image,
      publisher: { '@id': `${baseUrl}#Organization` },
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${baseUrl}/kleding?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    }
  }

  const formatDefault = () => {
    const route = useRoute()
    const config = useRuntimeConfig().public
    useHead({
      titleTemplate: titleChunk => titleChunk ? `${titleChunk} - Hengelose Revue` : 'Hengelose Revue - Dé hilarische theatershow in Twente',
      link: [
        {
          key: 'canonical',
          rel: 'canonical',
          href: `${baseUrl}${route.path}`
        },
        {
          key: 'preconnect-sb-cdn',
          rel: 'preconnect',
          href: 'https://a.storyblok.com/',
          crossorigin: 'anonymous'
        },
        {
          key: 'dns-prefetch-sb-cdn',
          rel: 'dns-prefetch',
          href: 'https://a.storyblok.com/',
          crossorigin: 'anonymous'
        },
        {
          key: 'preconnect-sb-api',
          rel: 'preconnect',
          href: 'https://api.storyblok.com/',
          crossorigin: 'anonymous'
        },
        {
          key: 'dns-prefetch-sb-api',
          rel: 'dns-prefetch',
          href: 'https://api.storyblok.com/',
          crossorigin: 'anonymous'
        }
      ],
      meta: [
        {
          key: 'robots',
          name: 'robots',
          content: config.stage !== 'live' ? 'noindex' : 'all'
        },
        {
          key: 'description',
          name: 'description',
          content: description
        },

        {
          key: 'og:title',
          property: 'og:title',
          content: 'Hengelose Revue - Dé hilarische theatershow in Twente'
        },
        {
          key: 'og:description',
          property: 'og:description',
          content: description
        },
        {
          key: 'og:site_name',
          property: 'og:site_name',
          content: 'Hengelose Revue'
        },
        {
          key: 'og:type',
          property: 'og:type',
          content: 'website'
        },
        {
          key: 'og:url',
          property: 'og:url',
          content: baseUrl
        },
        {
          key: 'og:logo',
          property: 'og:logo',
          content: `${baseUrl}/brand/logo/logo-fc.png`
        },
        {
          key: 'og:video',
          property: 'og:video',
          content: `${baseUrl}/hr-2024-video-lr.mp4`
        },
        {
          key: 'og:video:width',
          property: 'og:video:width',
          content: '853'
        },
        {
          key: 'og:video:height',
          property: 'og:video:height',
          content: '480'
        },
        {
          key: 'og:video:duration',
          property: 'og:video:duration',
          content: '67'
        },
        {
          key: 'og:image',
          property: 'og:image',
          content: `${baseUrl}/open-graph-watn-spul.jpg`
        },
        {
          key: 'og:image-2',
          property: 'og:image',
          content: `${baseUrl}/open-graph-2.jpg`
        },
        {
          key: 'og:image-3',
          property: 'og:image',
          content: `${baseUrl}/open-graph-3.jpg`
        },
        {
          key: 'og:image:width',
          property: 'og:image:width',
          content: '1200'
        },
        {
          key: 'og:image:height',
          property: 'og:image:height',
          content: '630'
        },
        {
          key: 'og:image:type',
          property: 'og:image:type',
          content: 'image/jpeg'
        },
        {
          key: 'twitter:card',
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          key: 'twitter:site',
          name: 'twitter:site',
          content: '@hengeloserevue'
        },
        {
          key: 'twitter:title',
          name: 'twitter:title',
          content: 'Hengelose Revue - Dé hilarische theatershow in Twente'
        },
        {
          key: 'twitter:description',
          name: 'twitter:description',
          content: description
        },
        {
          key: 'twitter:image',
          name: 'twitter:image',
          content: `${baseUrl}/open-graph-watn-spul.jpg`
        }
      ],
      htmlAttrs: {
        lang: 'nl'
      },
      script: [
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            ...publisher
          }
        }
      ]
    })
  }

  // "Wat ’n Spul, de nieuwe voorstelling van de Hengelose Revue: 29 januari t/m 5 februari 2027 in Schouwburg Hengelo. …"
  const showDescription = (events: Array<any>) => {
    const { whenLong } = usePerformances(events)
    return `${SHOW_TITLE}, de nieuwe voorstelling van de Hengelose Revue${whenLong ? `: ${whenLong} in Schouwburg Hengelo` : ''}. Humor, live muziek en dans door mensen uit Hengelo en omgeving.`
  }

  // One TheaterEvent per performance, so Google can list the dates
  const theaterEvents = (events: Array<any>) => {
    const eventDescription = showDescription(events)
    return events
      .filter(event => event.content?.date)
      .map(event => ({
        type: 'application/ld+json',
        innerHTML: {
          '@context': 'https://schema.org',
          '@type': 'TheaterEvent',
          name: `${SHOW_TITLE} - Hengelose Revue`,
          startDate: hengeloTime(event.content.date),
          endDate: hengeloTime(event.content.date, 150),
          description: eventDescription,
          image,
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: 'https://schema.org/EventScheduled',
          organizer: { '@id': `${baseUrl}#Organization` },
          inLanguage: 'nl-NL',
          typicalAgeRange: '12-85',
          location,
          ...(event.content.url?.cached_url
            ? {
                offers: {
                  '@type': 'Offer',
                  priceCurrency: 'EUR',
                  url: event.content.url.cached_url,
                  availability: 'https://schema.org/InStock',
                  price: TICKET_PRICE
                }
              }
            : {}),
          performer: performers
        }
      }))
  }

  const formatHome = ({ title, description, events } : {title: string, description: string, events: Array<any>}) => {
    const content = description || showDescription(events)
    useHead({
      title,
      meta: [
        { key: 'description', name: 'description', content },
        { key: 'og:description', property: 'og:description', content },
        { key: 'twitter:description', name: 'twitter:description', content }
      ],
      script: [
        website,
        ...theaterEvents(events)
      ]
    })
  }

  const formatEvent = ({ title, description, events }: {title: string, description: string, events: any}) => {
    const content = description || showDescription(events ?? [])
    useHead({
      title,
      meta: [
        { key: 'description', name: 'description', content },
        { key: 'og:description', property: 'og:description', content }
      ],
      script: theaterEvents(events ?? [])
    })
  }

  const formatAlbum = ({ title, description, items }:{title: string, description:string, items: any}) => {
    const route = useRoute()
    const img = useImage()
    const images = items.map((item: any) => {
      return {
        '@type': 'ImageObject',
        url: img(item.filename, { format: 'webp' }),
        caption: `${title} afbeelding ${item.id}`
      }
    })
    // Without a description in Storyblok: what the album is, in one sentence. Titles look like "2026 - Oost, West, Thuis...?"
    const [, year, show] = title?.match(/^(\d{4})\s*-\s*(.+)$/) ?? []
    const content = description || (show
      ? `${items.length} foto's van ${show}, de voorstelling van de Hengelose Revue uit ${year} in Schouwburg Hengelo: de spelers, de dans en het decor.`
      : `${items.length} foto's van ${title} bij de Hengelose Revue.`)
    useHead({
      title,
      meta: [
        { key: 'description', name: 'description', content },
        { key: 'og:description', property: 'og:description', content }
      ],
      script: [
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'ImageGallery',
            name: title,
            description: `Impressie van ${title}`,
            url: `${baseUrl}${route.fullPath}`,
            image: images,
            publisher
          }
        }
      ]
    })
  }

  const formatContact = ({ title, description }: {title: string, description: string}) => {
    useHead({
      title,
      meta: [{ key: 'description', name: 'description', content: description }],
      script: [
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${baseUrl}/contact`
            },
            url: `${baseUrl}/contact`,
            description,
            name: 'Contact - Hengelose Revue',
            inLanguage: 'nl-NL',
            image,
            publisher: { '@id': `${baseUrl}#Organization` }
          }
        }
      ]
    })
  }

  const formatNewsItem = ({ title, teaser, description, image, url, publishedAt, updatedAt }: {title: string, teaser: string, description: string, image:any, url:string, publishedAt?: string, updatedAt?: string}) => {
    const route = useRoute()
    const img = useImage()
    const absoluteImage = image?.filename ? img(image.filename, { format: 'webp', width: 1200, height: 633 }) : undefined
    // Without a teaser, the start of the article
    teaser = teaser || summarize(description)

    useHead({
      title,
      meta: [
        { key: 'description', name: 'description', content: teaser },
        {
          key: 'og:url',
          property: 'og:url',
          content: url
        },
        {
          key: 'og:title',
          property: 'og:title',
          content: title
        },
        {
          key: 'og:type',
          property: 'og:type',
          content: 'article'
        },
        {
          key: 'og:description',
          property: 'og:description',
          content: teaser
        },
        ...(absoluteImage
          ? [{
              key: 'og:image',
              property: 'og:image',
              content: absoluteImage
            }]
          : [])
      ],
      script: [
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: title,
            articleBody: description,
            url: `${baseUrl}${route.path}`,
            inLanguage: 'nl-NL',
            ...(absoluteImage ? { image: [absoluteImage] } : {}),
            ...(publishedAt ? { datePublished: publishedAt } : {}),
            ...(updatedAt ? { dateModified: updatedAt } : {}),
            author: { '@id': `${baseUrl}#Organization` },
            publisher: { '@id': `${baseUrl}#Organization` }
          }
        }
      ]
    })
  }

  const formatBreadcrumbs = () => {
    const { navigation } = useNavigation()
    const route = useRoute()
    const list :Ref<object[]> = ref([])

    list.value.push({
      '@type': 'ListItem',
      position: 1,
      name: 'Hengelose Revue',
      item: baseUrl
    })

    navigation.value.items.forEach(item => {
      if (route.fullPath.includes(item.parent.full_slug)) {
        // Add list item
        list.value.push({
          '@type': 'ListItem',
          position: 2,
          name: item.parent.name,
          item: `${baseUrl}/${item.parent.full_slug}`
        })

        item.items.forEach(child => {
          if (route.fullPath.includes(child.full_slug)) {
            list.value.push({
              '@type': 'ListItem',
              position: 3,
              name: child.name,
              item: `${baseUrl}/${child.full_slug}`
            })
          }
        })
      }
    })

    useHead({
      script: [
        website,
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: list.value
          }
        }
      ]
    })
  }

  const formatNewsItemBreadcrumbs = ({ name, url } : {name: string, url: string}) => {
    const { navigation } = useNavigation()
    const route = useRoute()
    const list :Ref<object[]> = ref([])

    list.value.push({
      '@type': 'ListItem',
      position: 1,
      name: 'Hengelose Revue',
      item: baseUrl
    })

    navigation.value.items.forEach(item => {
      if (route.fullPath.includes(item.parent.full_slug)) {
        // Add list item
        list.value.push({
          '@type': 'ListItem',
          position: 2,
          name: item.parent.name,
          item: `${baseUrl}/${item.parent.full_slug}`
        })

        item.items.forEach(child => {
          if (route.fullPath.includes(child.full_slug)) {
            list.value.push({
              '@type': 'ListItem',
              position: 3,
              name: child.name,
              item: `${baseUrl}/${child.full_slug}`
            })
          }
        })
      }
    })

    list.value.push({
      '@type': 'ListItem',
      position: 4,
      name,
      item: `${baseUrl}/${url}`
    })

    useHead({
      script: [
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: list.value
          }
        }
      ]
    })
  }

  const formatProduct = ({ product }: {
    product: {
      id: number
      slug: string
      title: string
      description?: string
      quantity: number,
      images?: (string | {fileId: string})[]
      categories?: Array<{
        id: number
        slug: string
        title: string
      }>
      colors?: Array<{ title: string }>
      themes?: Array<{ title: string }>
    }
  }) => {
    const route = useRoute()

    // Build the product URL dynamically from current route
    const productUrl = computed(() => {
      return `${baseUrl}${route.path}`
    })

    // Get the first image URL
    const productImage = computed(() => {
      const firstImage = product.images?.[0]
      if (!firstImage) { return null }
      const imageId = typeof firstImage === 'string' ? firstImage : firstImage.fileId
      return `${config.public.cdnUrl}/products/full/${imageId}.webp`
    })

    // For structured data: the description from the admin, or the one below
    const productDescription = computed(() => product.description || metaDescription.value)

    // For search results: always the same useful sentence, built from what the product is.
    // The admin description is left out here: it sometimes holds a note for the storage.
    const metaDescription = computed(() => {
      const colors = (product.colors ?? []).map(color => color.title.trim().toLowerCase())
      const themes = (product.themes ?? []).map(theme => themeInSentence(theme.title)).filter(theme => !LABEL_THEMES.includes(theme.toLowerCase()))
      return summarize([
        `${product.title} huren in Hengelo bij de Hengelose Revue.`,
        colors.length ? `Kleur: ${listNl(colors)}.` : '',
        themes.length ? `Thema: ${listNl(themes)}.` : '',
        product.quantity > 1 ? `${product.quantity} op voorraad, vanaf €6,-.` : 'Vanaf €6,-.'
      ].filter(Boolean).join(' '))
    })

    useHead({
      title: `${product.title} huren`,
      meta: [
        {
          key: 'description',
          name: 'description',
          content: metaDescription.value
        },
        // OpenGraph
        {
          key: 'og:title',
          property: 'og:title',
          content: `${product.title} huren | vanaf €6,-`
        },
        {
          key: 'og:description',
          property: 'og:description',
          content: metaDescription.value
        },
        {
          key: 'og:type',
          property: 'og:type',
          content: 'product'
        },
        {
          key: 'og:url',
          property: 'og:url',
          content: productUrl.value
        },
        ...(productImage.value
          ? [{
              key: 'og:image',
              property: 'og:image',
              content: productImage.value
            }]
          : []),
        // Twitter Card
        {
          name: 'twitter:card',
          content: 'summary_large_image'
        },
        {
          name: 'twitter:title',
          content: product.title
        },
        {
          name: 'twitter:description',
          content: metaDescription.value
        },
        ...(productImage.value
          ? [{
              name: 'twitter:image',
              content: productImage.value
            }]
          : []),
        // Product-specific OpenGraph
        {
          property: 'product:price:amount',
          content: '6.00'
        },
        {
          property: 'product:price:currency',
          content: 'EUR'
        }
      ],
      script: [
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product.title,
            description: productDescription.value,
            ...(productImage.value ? { image: productImage.value } : {}),
            brand: {
              '@type': 'Organization',
              name: 'Hengelose Revue'
            },
            offers: {
              '@type': 'Offer',
              price: '6.00',
              priceCurrency: 'EUR',
              offerCount: product.quantity,
              priceSpecification: {
                '@type': 'UnitPriceSpecification',
                price: '6.00',
                priceCurrency: 'EUR',
                unitText: 'dag',
                referenceQuantity: {
                  '@type': 'QuantitativeValue',
                  value: '1',
                  unitText: 'dag'
                }
              },
              availability: 'https://schema.org/InStock',
              url: productUrl.value,
              seller: {
                '@type': 'Organization',
                name: 'Hengelose Revue'
              }
            },
            ...(product.categories?.length
              ? {
                  category: product.categories.map(c => c.title).join(', ')
                }
              : {})
          }
        },
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Hengelose Revue', item: baseUrl },
              { '@type': 'ListItem', position: 2, name: 'Kleding verhuur', item: `${baseUrl}/kleding` },
              ...(product.categories?.[0]
                ? [{ '@type': 'ListItem', position: 3, name: product.categories[0].title, item: `${baseUrl}/kleding/${product.categories[0].slug}` }]
                : []),
              { '@type': 'ListItem', position: product.categories?.[0] ? 4 : 3, name: product.title, item: productUrl.value }
            ]
          }
        },
        website
      ]
    })
  }

  const formatCategory = ({ category, products, total }: { category: Ref<any>, products: Ref<any[]>, total?: Ref<number> }) => {
    const listItems = products.value.map((product, index) => {
      if (!product.images.length) { return false }
      return {

        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: product.title,
          url: `${baseUrl}/kleding/product/${product.slug}/${product.id}`,
          image: product.images.length ? `${config.public.cdnUrl}/products/thumbs/${product.images[0]}.webp` : null,
          description: product.description,
          offers: {
            '@type': 'Offer',
            price: '6.00',
            priceCurrency: 'EUR',
            offerCount: product.quantity,
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '6.00',
              priceCurrency: 'EUR',
              unitText: 'dag',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: product.quantity,
                unitText: 'dag'
              }
            },
            availability: 'https://schema.org/InStock',
            url: `${baseUrl}/kleding/product/${product.slug}/${product.id}`,
            seller: publisher
          }
        }
      }
    }).filter(Boolean)
    const mainEntity = {
      '@type': 'ItemList',
      itemListElement: listItems
    }

    const list = [
      { '@type': 'ListItem', position: 1, name: 'Hengelose Revue', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: 'Kleding verhuur', item: `${baseUrl}/kleding` },
      { '@type': 'ListItem', position: 3, name: category.value.title, item: `${baseUrl}/kleding/${category.value.slug}` }
    ]

    // "Jurkjes huren in Hengelo: 87 stuks, onder meer voor disco, glitters en carnaval. …"
    // The themes that occur most in this category make each description its own.
    const themeCounts = new Map<string, number>()
    for (const product of products.value) {
      for (const theme of product.themes ?? []) {
        const title = themeInSentence(theme.title)
        if (!LABEL_THEMES.includes(title.toLowerCase())) { themeCounts.set(title, (themeCounts.get(title) ?? 0) + 1) }
      }
    }
    const topThemes = [...themeCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([title]) => title)
    const count = total?.value ?? products.value.length
    const categoryDescription = category.value.description || summarize([
      `${category.value.title} huren in Hengelo: ${count} ${count === 1 ? 'stuk' : 'stuks'} uit de voorstellingen van de Hengelose Revue`,
      topThemes.length ? `, onder meer voor ${listNl(topThemes)}.` : '.',
      ' Vanaf €6,-.'
    ].join(''))
    const shareImage = products.value.find(product => product.images?.length)?.images?.[0]

    useHead({
      title: `${category.value.title} huren in Hengelo`,
      meta: [
        {
          key: 'description',
          name: 'description',
          content: categoryDescription
        },
        {
          key: 'og:description',
          property: 'og:description',
          content: categoryDescription
        },
        {
          key: 'og:title',
          property: 'og:title',
          content: `${category.value.title} huren in Hengelo | Hengelose Revue`
        },
        {
          name: 'og:locale',
          property: 'og:locale',
          content: 'nl'
        },
        ...(shareImage
          ? [{
              key: 'og:image',
              property: 'og:image',
              content: `${config.public.cdnUrl}/products/full/${shareImage}.webp`
            }]
          : [])
      ],
      script: [
        website,
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            description: categoryDescription,
            name: category.value.title,
            mainEntity
          }
        },
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: list
          }
        }
      ]
    })
  }

  const formatFaq = ({ title, description, faqs }: { title: string, description: string, faqs: Array<{ question: string, answer: string }> }) => {
    useHead({
      title,
      meta: [{ key: 'description', name: 'description', content: description }],
      script: [
        website,
        {
          type: 'application/ld+json',
          innerHTML: {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map(({ question, answer }) => ({
              '@type': 'Question',
              name: question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: answer
              }
            }))
          }
        }
      ]
    })
  }

  return { formatDefault, formatHome, formatEvent, formatAlbum, formatContact, formatNewsItem, formatNewsItemBreadcrumbs, formatBreadcrumbs, formatProduct, formatCategory, formatFaq, website }
}
